import React, { Component } from 'react';
import { Engine, Scene, SceneLoader, ArcRotateCamera, HemisphericLight, Vector3, Color3, Color4, DracoCompression, CubeTexture, Texture, StandardMaterial, Mesh, PBRMaterial, PointerEventTypes, MeshBuilder, Animation, PhotoDome, HDRCubeTexture, TransformNode, AnimationGroup, Scalar, DefaultRenderingPipeline, ImageProcessingConfiguration} from '@babylonjs/core';
import '@babylonjs/loaders'; // Import loaders for .glb files
import { BulletData } from "./../data/bullet.data";
import { ConfigData } from "./../data/config.data";
import { ProductData } from "./../data/product.data";
import { ColorData } from "./../data/color.data";
import GetBrowserInfo from './helpers/GetBrowserInfo';

const configData: any = ConfigData();
const bulletData: any = BulletData();
const productData: any = ProductData();
const colorData: any = ColorData();
const browserInfo: any = GetBrowserInfo();

interface Scene3D {
  canvasRef: any,
  globalEngine: Engine | null,
  globalScene: Scene | null,
  globalCamera: ArcRotateCamera | null,
  globalCameraFOV : number,
  globalScenario: Mesh | null,
  globalDome: PhotoDome | null,
  globalDomeFade: PhotoDome | null,
  globalDomeSpot: string,
  globalDomeHotspots: any,
  globalDomeTextures: any,
  globalDomeTransition: number
  globalRoof: Mesh | null,
  globalFacade: Mesh | null,
  globalScenarioConfig: any,
  globalSoloModel: Mesh | null,
  globalSoloModelAnimations: any,
  globalSoloModelColors: any,
  globalBullets: any,
  globalLight: HemisphericLight | null,
  globalRenderingPipeline: DefaultRenderingPipeline | null,
  globalCameraUp: number;
  globalCameraRight: number;
  globalPerformanceControl: {
    scalingLevel: number
    framesCount: number
    avgFrameTime: number
    minScalingLevel: number
    maxScalingLevel: number
    targetFramerate: number
    frameSamples: number
  }
}

interface Props {
  menuView: string;
  isLandscape: boolean;
  scenario: any;
  roofProduct: any;
  roofColor: any;
  facadeProduct: any;
  facadeColor: any;
  productCategory: string;
  showSoloViewBullet: boolean;
  bulletSelected: any;
  showProductAnimation: boolean;
  setScenario: (value: any) => void,
  setShowLoadingScreen: (value: boolean) => void,
  setShowProductAnimation: (value: boolean) => void,
  setShowActiveBullet: (value: string) => void,
}

interface State {
  sceneLoaded: boolean,
  isAnimatingProduct: boolean,
}
class Scene3D extends Component<Props, State> {

  private initialPinchDistance: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      sceneLoaded: false,
      isAnimatingProduct: false,
    }
    this.canvasRef = React.createRef();
    this.globalScenario = null;
    this.globalRoof = null;
    this.globalFacade = null;
    this.globalScenarioConfig = null;
    this.globalDome = null
    this.globalDomeFade = null
    this.globalDomeTextures = null
    this.globalDomeHotspots = null
    this.globalDomeTransition = -1
    this.globalBullets = null;
    this.globalDomeSpot = "space01"
    this.globalSoloModel = null;
    this.globalLight = null;
    this.globalRenderingPipeline = null;
    this.globalSoloModelAnimations = null;
    this.globalSoloModelColors = null;
    this.globalCameraFOV = 0.8;
    this.globalCameraUp = 0;
    this.globalCameraRight = 0;

    this.globalPerformanceControl = {
      scalingLevel: 1.2,
      framesCount: 0,
      avgFrameTime: 0,
      minScalingLevel: 1.0,//quality at original size
      maxScalingLevel: 1.5,//quality at half size
      targetFramerate: 30,
      frameSamples: 60,
    }
  }

  componentDidMount(): void {
    if (this.canvasRef !== null) {
      //SETUP ENGINE
      this.globalEngine = new Engine(this.canvasRef.current, true);
      this.globalEngine.disableUniformBuffers = (browserInfo.isOSiOS === true || browserInfo.isOSMacOS === true)
      this.globalEngine.setHardwareScalingLevel(this.globalPerformanceControl.scalingLevel);
      DracoCompression.Configuration.decoder = {
        wasmUrl: "./draco/draco_decoder.js",
        wasmBinaryUrl: "./draco/draco_decoder.wasm",
        fallbackUrl: "./draco/draco_wasm_wrapper.js",
      };
      this.CreateScene();
    }
    window.addEventListener('resize', this.handleResize)
    if (this.canvasRef.current) {
      this.canvasRef.current.addEventListener('touchstart', this.onTouchStart);
      this.canvasRef.current.addEventListener('touchmove', this.onTouchMove);
      this.canvasRef.current.addEventListener('touchend', this.onTouchEnd);
    }
    window.addEventListener('keydown', this.handleKeysDown)
    window.addEventListener('keyup', this.handleKeysUp)
  }

  componentWillUnmount(): void {
    if (this.globalScene)
      this.globalScene.dispose()
    if (this.globalEngine)
      this.globalEngine.dispose()
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeysDown)
    window.removeEventListener('keyup', this.handleKeysUp)
    if (this.canvasRef.current) {
      this.canvasRef.current.removeEventListener('touchstart', this.onTouchStart);
      this.canvasRef.current.removeEventListener('touchmove', this.onTouchMove);
      this.canvasRef.current.removeEventListener('touchend', this.onTouchEnd);
    }
  }

  onTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 2) {
      this.initialPinchDistance = this.getPinchDistance(event.touches);
    }
  };

  onTouchMove = (event: TouchEvent) => {
    if (event.touches.length === 2 && this.initialPinchDistance !== null) {
      const currentPinchDistance = this.getPinchDistance(event.touches);
      const pinchDelta = currentPinchDistance - this.initialPinchDistance;
      const newFov = this.globalCameraFOV - pinchDelta * 0.001; // Adjust sensitivity factor as needed
      if (this.globalCamera && this.globalScenarioConfig.is360 === true && this.props.showSoloViewBullet === false) {
        this.globalCamera.fov = Math.max(30 * Math.PI / 180, Math.min(70 * Math.PI / 180, newFov)); // Limit FOV range
      };
    }
  };

  onTouchEnd = () => {
    this.initialPinchDistance = null;
    if (this.globalCamera) {
      this.globalCameraFOV = this.globalCamera.fov
    }
  };

  getPinchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.roofProduct !== this.props.roofProduct) {
      this.UpdateCategoryProduct(this.globalRoof, this.props.roofProduct, this.props.roofColor)
      this.UpdateActiveBullets(this.globalScenarioConfig.is360 !== true)
    }
    if (prevProps.roofColor !== this.props.roofColor) {
      this.UpdateCategoryProduct(this.globalRoof, this.props.roofProduct, this.props.roofColor)
      this.UpdateActiveBullets(this.globalScenarioConfig.is360 !== true)
    }
    if (prevProps.facadeProduct !== this.props.facadeProduct) {
      this.UpdateCategoryProduct(this.globalFacade, this.props.facadeProduct, this.props.facadeColor)
      this.UpdateActiveBullets(this.globalScenarioConfig.is360 !== true)
    }
    if (prevProps.facadeColor !== this.props.facadeColor) {
      this.UpdateCategoryProduct(this.globalFacade, this.props.facadeProduct, this.props.facadeColor)
      this.UpdateActiveBullets(this.globalScenarioConfig.is360 !== true)
    }
    if (prevProps.productCategory !== this.props.productCategory) {
      const product = this.props.productCategory === "roof" ? this.props.roofProduct : this.props.facadeProduct;
      const color = this.props.productCategory === "roof" ? this.props.roofColor : this.props.facadeColor;
      const models = this.props.productCategory === "roof" ? this.globalRoof : this.globalFacade;
      this.UpdateCategoryProduct(models, product, color)
      this.UpdateActiveBullets(this.globalScenarioConfig.is360 !== true)
    }

    if (prevProps.scenario !== this.props.scenario) {
      if(this.globalScenarioConfig === null || (this.globalScenarioConfig !== null && this.globalScenarioConfig.id !== this.props.scenario.id)){
        this.CreateScene();
      }
    }
    if (prevProps.showSoloViewBullet !== this.props.showSoloViewBullet) {
      this.ShowSoloView(this.props.showSoloViewBullet)
    }
    if (prevProps.bulletSelected !== this.props.bulletSelected) {
      this.BulletSelectedOnUI(this.props.bulletSelected)
    }
    if (prevProps.showProductAnimation !== this.props.showProductAnimation) {
      if(this.props.showProductAnimation === true){
        this.BeginAnimatingSoloProduct()
      }else{
        this.ForceStopAnimatingSoloProduct(true)
        if(this.props.menuView !== "VISTA 3D")
          this.ShowSoloView(false)
      }
    }
    if (prevProps.menuView !== this.props.menuView && this.props.menuView !== "VISTA 3D") {
      this.ForceStopAnimatingSoloProduct(true)
      this.ShowSoloView(false)
    }
  }

  CreateScene = () => {
    if(this.globalScene)
      this.globalScene.dispose();
    
    this.setState({ sceneLoaded: false })
    this.props.setShowLoadingScreen(true)
    this.globalRoof = null
    this.globalFacade = null
    this.globalDome = null
    this.globalDomeFade = null
    this.globalDomeTextures = null

    if(this.globalEngine === null)
      return
    if(this.props.scenario === null)
      return

    this.globalScenarioConfig = this.props.scenario

    //SETUP ENGINE
    this.globalScene = new Scene(this.globalEngine);
    this.globalScene.clearColor = new Color4(1, 1, 1, 1);

    //SETUP CAMERA
    this.globalCamera = new ArcRotateCamera("MainCamera", this.globalScenarioConfig.startingcoords.alpha * Math.PI / 180, this.globalScenarioConfig.startingcoords.beta * Math.PI / 180, this.globalScenarioConfig.startingcoords.radius, new Vector3(this.globalScenarioConfig.target.x, this.globalScenarioConfig.target.y, this.globalScenarioConfig.target.z), this.globalScene);
    this.globalCamera.lockedTarget = new Vector3(this.globalScenarioConfig.target.x, this.globalScenarioConfig.target.y, this.globalScenarioConfig.target.z)
    this.globalCamera.fov = this.globalScenarioConfig.camerafov * Math.PI / 180
    this.globalCamera.lowerRadiusLimit = this.globalScenarioConfig.limitradius.lower
    this.globalCamera.upperRadiusLimit = this.globalScenarioConfig.limitradius.upper
    this.globalCamera.upperBetaLimit = this.globalScenarioConfig.limitbeta.upper * Math.PI / 180
    this.globalCamera.attachControl(this.canvasRef.current, true);
    this.globalCameraFOV = this.globalScenarioConfig.camerafov * Math.PI / 180;
    // this.globalCamera.wheelDeltaPercentage = 0.1;
    // this.globalCamera.pinchDeltaPercentage = 0.1;
    
    this.globalRenderingPipeline = new DefaultRenderingPipeline("default", false, this.globalScene, [this.globalCamera]);
    this.globalRenderingPipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    this.globalRenderingPipeline.imageProcessing.toneMappingEnabled = true;
    this.globalRenderingPipeline.imageProcessing.contrast = configData.contrast;
    this.globalRenderingPipeline.imageProcessing.exposure = configData.exposure

    //SETUP LIGHTING
    this.globalLight = new HemisphericLight("light", new Vector3(1, 1, 0), this.globalScene);
    this.globalLight.intensity = configData.lightIntensityScene
    this.globalLight.diffuse = new Color3(1,1,1)

    this.LoadAllTexturesAndModels(this.globalScene, this.globalScenarioConfig);

    // Event listener for clicks and mouse wheel
    this.globalScene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.pickInfo?.hit) {
        const pickedMesh = pointerInfo.pickInfo.pickedMesh;
        if (pointerInfo.type === PointerEventTypes.POINTERPICK && pickedMesh) {
          this.handleClickedBulletOrHotspot(pickedMesh as Mesh)
        }
      }
      if(pointerInfo.type === PointerEventTypes.POINTERWHEEL){
        const event = pointerInfo.event as WheelEvent;
        if(this.globalCamera && this.globalScenarioConfig.is360 === true && this.props.showSoloViewBullet === false){
          this.globalCamera.fov = this.globalCamera.fov + 0.05 * event.deltaY * Math.PI / 180
          if(this.globalCamera.fov < 30 * Math.PI / 180){ this.globalCamera.fov = 30 * Math.PI / 180; }
          if(this.globalCamera.fov > 70 * Math.PI / 180){ this.globalCamera.fov = 70 * Math.PI / 180; }
        }
      }
    });

    // HOVER OBJECT
    // this.globalScene.onPointerMove = (evt) => {
    //   if(this.globalScene){
    //     const pickResult = this.globalScene.pick(this.globalScene.pointerX, this.globalScene.pointerY);
    //     if (pickResult?.hit) {
    //       console.log("hover mesh")
    //       console.log(pickResult.pickedMesh)
    //     }
    //   }
    // };
    

    this.globalScene.onBeforeRenderObservable.add(() => {
      if (this.globalScene)
        this.frameUpdate(0.001 * this.globalScene.deltaTime)
    });

    this.globalEngine.runRenderLoop(() => {
      this.globalScene?.render();
    });
  }

  handleKeysDown = (event: any) => {
    switch (event.key) {
      case "ArrowLeft":
        this.globalCameraRight = 1;
        break;
      case "ArrowRight":
        this.globalCameraRight = -1;
        break;
      case "ArrowUp":
        this.globalCameraUp = 1;
        break;
      case "ArrowDown":
        this.globalCameraUp = -1;
        break;
    }
  }

  handleKeysUp = (event: any) => {
    switch (event.key) {
      case "ArrowLeft":
        this.globalCameraRight = 0;
        break;
      case "ArrowRight":
        this.globalCameraRight = 0;
        break;
      case "ArrowUp":
        this.globalCameraUp = 0;
        break;
      case "ArrowDown":
        this.globalCameraUp = 0;
        break;
    }
  }

  handleResize = () => {
    this.globalEngine?.resize();
  }

  handleClickedBulletOrHotspot = (clickedMesh : Mesh) => {
    let isBulletMesh = false
    for(const bulletKey in this.globalBullets){
      for(const bullet of this.globalBullets[bulletKey]){
        if(bullet === clickedMesh){
          isBulletMesh = true
          break;
        }
      }
      if(isBulletMesh === true)
        break;
    }
    if(isBulletMesh === true)
      this.props.setShowActiveBullet(clickedMesh.name)

    let isHotspotMesh = false
    for(const hotspotKey in this.globalDomeHotspots){
      for(const hotspot of this.globalDomeHotspots[hotspotKey].hotspots){
        if(hotspot === clickedMesh){
          isHotspotMesh = true
          break;
        }
      }
      if(isHotspotMesh === true)
        break;
    }
    if(isHotspotMesh === true){
      this.UpdateActiveHotspots(clickedMesh.name, 1)
    }
  }

  UpdateActiveHotspots = (spot : string, transitionTime : number) => {
    if(this.globalDome === null || this.globalDomeFade === null)
      return;
    
    this.globalDomeSpot = spot
    let texture = this.globalDomeTextures[this.props.roofProduct.id][this.props.roofColor.id][this.globalDomeSpot];
    this.globalDome.photoTexture = texture
    this.globalDomeTransition = transitionTime

    for(const hotspotKey in this.globalDomeHotspots){
      const enableBullet = hotspotKey.toLowerCase() === this.globalDomeSpot
      for(const hotspotMesh of this.globalDomeHotspots[hotspotKey].hotspots){
        hotspotMesh.setEnabled(enableBullet)
      }
      if(enableBullet === true && this.globalCamera)
        this.globalCamera.alpha =  this.globalDomeHotspots[hotspotKey].rotation * Math.PI / 180
    }
  }

  UpdateActiveBullets = (value: Boolean) => {
    const product = this.props.productCategory === "roof" ? this.props.roofProduct : this.props.facadeProduct;
    if(!product)
      return;

    for(const bulletKey in this.globalBullets){
      const enableBullet = product.id.toLowerCase() === bulletKey.toLowerCase()
      for(const bulletMesh of this.globalBullets[bulletKey]){
        bulletMesh.setEnabled(enableBullet && value)
      }
    }
  }

  BulletSelectedOnUI = (bulletui: any) => {
    const bulletid: string = bulletui === null ? "" : bulletui.id;
    
    for(const bulletKey in this.globalBullets){
      for(const bullet of this.globalBullets[bulletKey]){
        if(bullet.name === bulletid){
          const clickedMaterial = bullet.material.clone("bulletClicked");
          clickedMaterial.emissiveColor = new Color3(0,1,0)
          bullet.material = clickedMaterial
        }else if(bullet.material.emissiveColor.r === 0 && bullet.material.emissiveColor.g === 1 && bullet.material.emissiveColor.b === 0){
          const readMaterial = bullet.material.clone("bulletRead");
          readMaterial.emissiveColor = new Color3(0,96/255,174/255)
          bullet.material.dispose()
          bullet.material = readMaterial
        }
      }
    }
  }

  ShowSoloView = (isSoloShow: boolean) => {
    if(!this.globalCamera || !this.globalSoloModel)
      return;

    if(this.globalDomeFade && this.globalDome){
      this.globalDomeFade.setEnabled(!isSoloShow)
      this.globalDome.setEnabled(!isSoloShow)
      for(const spotKey in this.globalDomeHotspots){
        const enableBullet = !isSoloShow && spotKey.toLowerCase() === this.globalDomeSpot
        for(const hotspot of this.globalDomeHotspots[spotKey].hotspots){
          hotspot.setEnabled(enableBullet)
        }
      }
    }

    if(this.globalScenarioConfig.is360 === true){
      if(isSoloShow === true)
        this.globalCamera.inputs.attached.mousewheel.attachControl();
      else
        this.globalCamera.inputs.attached.mousewheel.detachControl();    
    }

    const product = this.props.productCategory === "roof" ? this.props.roofProduct : this.props.facadeProduct;
    const color = this.props.productCategory === "roof" ? this.props.roofColor : this.props.facadeColor;
    
    this.UpdateSoloProduct(this.globalSoloModel, product, color)
    if(!this.globalRenderingPipeline || !this.globalLight || !this.globalScene)
      return;
    if(isSoloShow === true){ //Solo product view
      if(this.globalScenarioConfig.is360 === true){
        this.globalCamera.keysUp = [38]; // Tecla flecha arriba
        this.globalCamera.keysDown = [40]; // Tecla flecha abajo
        this.globalCamera.keysLeft = [37]; // Tecla flecha izquierda
        this.globalCamera.keysRight = [39]; // Tecla flecha derecha
        this.globalCamera.angularSensibilityX = 1000
        this.globalCamera.angularSensibilityY = 1000
      }
      this.globalScene.environmentIntensity = configData.environmentIntensitySolo
      this.globalRenderingPipeline.imageProcessing.toneMappingEnabled = false;
      this.globalRenderingPipeline.imageProcessing.contrast = configData.contrastSolo;
      this.globalRenderingPipeline.imageProcessing.exposure = configData.exposureSolo;
      this.globalLight.intensity = configData.lightIntensitySoloView
      this.globalScenario?.setEnabled(false)
      this.globalSoloModel.setEnabled(true)
      this.globalCamera.alpha = configData.bulletstartingcoords.alpha * Math.PI / 180
      this.globalCamera.beta = configData.bulletstartingcoords.beta * Math.PI / 180
      this.globalCamera.radius = configData.bulletstartingcoords.radius
      this.globalCamera.lockedTarget = new Vector3(configData.bullettarget.x, configData.bullettarget.y, configData.bullettarget.z)
      this.globalCamera.fov = this.props.isLandscape ? configData.bulletCameraFOV * Math.PI / 180 : configData.portraitBulletCameraFOV * Math.PI / 180
      this.globalCamera.lowerRadiusLimit = configData.bulletlimitradius.lower
      this.globalCamera.upperRadiusLimit = configData.bulletlimitradius.upper
      this.globalCamera.upperBetaLimit = configData.bulletlimitbeta.upper * Math.PI / 180
      
      for (const meshProduct of this.globalSoloModel.getChildren()) {
        for (const childColor of meshProduct.getChildren()) {
          if(childColor.name.includes("staticdecals")){
            for (const decal of childColor.getChildren()) {
              const enabled = decal.name.toLowerCase().includes(color.decal)
              decal.setEnabled(enabled)
            }
          }
        }
      }
      this.UpdateActiveBullets(true)
    }else{ //Scenario view
      if(this.globalScenarioConfig.is360 === true){
        this.globalCamera.keysUp = [];
        this.globalCamera.keysDown = [];
        this.globalCamera.keysLeft = [];
        this.globalCamera.keysRight = [];
        this.globalCamera.angularSensibilityX = 5000
        this.globalCamera.angularSensibilityY = 5000
      }

      this.globalScene.environmentIntensity = configData.environmentIntensity
      this.globalRenderingPipeline.imageProcessing.toneMappingEnabled = true;
      this.globalRenderingPipeline.imageProcessing.contrast = configData.contrast;
      this.globalRenderingPipeline.imageProcessing.exposure = configData.exposure;
      this.globalLight.intensity = configData.lightIntensityScene
      this.globalScenario?.setEnabled(true)
      this.globalSoloModel.setEnabled(false)

      this.globalCamera.alpha = this.globalScenarioConfig.is360 === true ? this.globalCamera.alpha = this.globalDomeHotspots[this.globalDomeSpot].rotation * Math.PI / 180 : this.globalScenarioConfig.startingcoords.alpha * Math.PI / 180
      this.globalCamera.beta = this.globalScenarioConfig.startingcoords.beta * Math.PI / 180
      this.globalCamera.radius = this.globalScenarioConfig.startingcoords.radius
      this.globalCamera.lockedTarget = new Vector3(this.globalScenarioConfig.target.x, this.globalScenarioConfig.target.y, this.globalScenarioConfig.target.z)
      this.globalCamera.fov = this.globalScenarioConfig.camerafov * Math.PI / 180
      this.globalCamera.lowerRadiusLimit = this.globalScenarioConfig.limitradius.lower
      this.globalCamera.upperRadiusLimit = this.globalScenarioConfig.limitradius.upper
      this.globalCamera.upperBetaLimit = this.globalScenarioConfig.limitbeta.upper * Math.PI / 180
      this.globalCameraFOV = this.globalScenarioConfig.camerafov * Math.PI / 180;

      //reset all bullets color to white
      for(const bulletKey in this.globalBullets){
        for(const bullet of this.globalBullets[bulletKey]){
          bullet.material.emissiveColor = new Color3(1,1,1)
        }
      }
      this.UpdateActiveBullets(false)
    }
  }

  UpdateSoloProduct = (parentMesh: Mesh | null, product : any | null, color : any | null) => {
    if(product === null || parentMesh === null || this.globalSoloModelColors === null)
      return;

    const productColorConfig = this.globalSoloModelColors[product.id]
    if(productColorConfig){
      const meshes : Mesh[] = productColorConfig['meshes']
      const texture = productColorConfig[color.id]
      if(meshes && texture){
        for(let mesh of meshes){
          if(mesh.material instanceof PBRMaterial)
            mesh.material.albedoTexture = texture
          if(mesh.material instanceof StandardMaterial)
            mesh.material.diffuseTexture = texture
        }
      }
    }

    for(let meshProduct of parentMesh.getChildren()){
      const enableProduct: boolean = meshProduct.name.toLowerCase().replace(" ","").includes( product.id.toLowerCase().replace(" ","") );
      meshProduct.setEnabled(enableProduct);
    }
  }

  UpdateCategoryProduct = (parentMesh: Mesh | null, product : any | null, color : any | null) => {
    if(product === null)
      return;
    if(this.globalScenarioConfig.is360 === false && parentMesh !== null){
      for (let p = 0; p < parentMesh.getChildren().length; p++) {
        const meshProduct = parentMesh.getChildren()[p] as Mesh;
        const enableProduct: boolean = meshProduct.name.toLowerCase().replace(" ","").includes( product.id.toLowerCase().replace(" ","") )
        meshProduct.setEnabled(enableProduct)
  
        if( enableProduct === true && color !== null ){
          let colorCode = Color3.FromHexString(color.color);
          this.SetMaterialColor(meshProduct.material, colorCode);
          for (let c = 0; c < meshProduct.getChildren().length; c++) {
            const childCustom = meshProduct.getChildren()[c] as Mesh;
            if(childCustom.name.toLowerCase().includes("custom")){
              this.SetMaterialColor(childCustom.material, colorCode);
            }
          }
        }
      }
    }else if(this.globalDome && this.globalDomeFade){
      let texture = this.globalDomeTextures[product.id][color.id][this.globalDomeSpot];
      this.globalDome.photoTexture = texture
      this.globalDomeFade.photoTexture = texture
    }
    
  }
  SetMaterialColor = (material: any, color: Color3) => {
    if(!material)
      return;
    
    if(material instanceof PBRMaterial)
      material.albedoColor = color
    if(material instanceof StandardMaterial)
      material.diffuseColor = color
  }

  BeginAnimatingSoloProduct = () => {
    if(!this.globalCamera)
      return;
    this.globalCamera.detachControl()
    this.globalCamera.upperRadiusLimit = 100
    this.globalSoloModelAnimations[this.props.roofProduct.id]['duplicated'].setEnabled(false)
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationTime'] = 3
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationDeltaTime'] = 0
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationState'] = 1
    this.globalSoloModelAnimations[this.props.roofProduct.id]['usercameraposition'] = this.globalCamera.position.clone()
    this.UpdateActiveBullets(false)
  }

  EndAnimationSoloProduct = () => {
    if(!this.globalCamera)
      return;
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationDeltaTime'] = 0
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationState'] = 3
  }

  ForceStopAnimatingSoloProduct = (resetCamera: boolean) => {
    if(!this.globalCamera)
      return;
    if(resetCamera === true){
      this.globalSoloModelAnimations[this.props.roofProduct.id]['animationGroup'].reset()
      this.globalSoloModelAnimations[this.props.roofProduct.id]['animationGroup'].stop()
      this.ShowSoloView(true)
    }

    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationState'] = 0
    this.globalSoloModelAnimations[this.props.roofProduct.id]['animationDeltaTime'] = 0
    this.UpdateActiveBullets(true)
    this.globalCamera.upperRadiusLimit = configData.bulletlimitradius.upper
    this.globalCamera.attachControl(this.canvasRef.current, true);
    this.props.setShowProductAnimation(false)
  }

  Lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }

  frameUpdate = (deltaTime: number): void => {
    if (!this.props.roofProduct || !this.globalEngine || !this.globalScene || !this.globalCamera || !this.globalPerformanceControl || !this.globalBullets || !this.globalSoloModelAnimations) // || !this.globalDome || !this.globalDomeFade
      return;

    //console.log("alpha: " + (this.globalCamera.alpha * 180 / Math.PI) + " beta: " + (this.globalCamera.beta * 180 / Math.PI) + " radius: " + this.globalCamera.radius)
    for(const bulletKey in this.globalBullets){
      for(const bulletMesh of this.globalBullets[bulletKey]){
        this.LookAtCamera(bulletMesh, this.globalCamera )
      }
    }

    if(this.globalDomeHotspots){
      for(const hotspotKey in this.globalDomeHotspots){
        for(const hotspotMesh of this.globalDomeHotspots[hotspotKey].hotspots){
          this.LookAtCamera(hotspotMesh, this.globalCamera )
        }
      }
    }
    
    if(this.globalDomeTransition > 0 && this.globalDome && this.globalDomeFade){
      this.globalDomeTransition -= deltaTime
      
      if(this.globalDomeTransition < 0){
        this.globalDomeTransition = -1
        this.globalDomeFade.photoTexture = this.globalDome.photoTexture
      }
      if(this.globalDomeFade.mesh.material)
        this.globalDomeFade.mesh.material.alpha = this.globalDomeTransition;
    }

    const productID = this.props.roofProduct.id;
    if(this.globalSoloModelAnimations[productID] !== undefined){
      
      let animationState = this.globalSoloModelAnimations[productID]['animationState']
      let animationTime = this.globalSoloModelAnimations[productID]['animationTime']
      let animationGroup: AnimationGroup = this.globalSoloModelAnimations[productID]['animationGroup']
      let cameraPosition: TransformNode = this.globalSoloModelAnimations[productID]['cameraposition']
      let cameraTarget: TransformNode = this.globalSoloModelAnimations[productID]['cameratarget']
      let cameraFov: TransformNode = this.globalSoloModelAnimations[productID]['camerafov']
      let cameraSync: TransformNode = this.globalSoloModelAnimations[productID]['camerasync']

      let cameraStartPosition: Vector3 = this.globalSoloModelAnimations[productID]['startcameraposition']
      let cameraStartTarget: Vector3 = this.globalSoloModelAnimations[productID]['startcameratarget']
      let cameraStartFov: Vector3 = this.globalSoloModelAnimations[productID]['startcamerafov']
      let cameraUserPosition: Vector3 = this.globalSoloModelAnimations[productID]['usercameraposition']

      this.globalSoloModelAnimations[productID]['animationDeltaTime'] += deltaTime
      
      if(animationState === 1){
        //Begin Animation
        this.globalCamera.lowerRadiusLimit = 0.1
        this.globalCamera.upperRadiusLimit = 1000
        
        this.globalCamera.target = Vector3.Lerp(this.globalCamera.target, cameraStartTarget, 1.2 * deltaTime)
        this.globalCamera.lockedTarget = Vector3.Lerp(this.globalCamera.lockedTarget, cameraStartTarget, 1.2 * deltaTime)
        this.globalCamera.position = Vector3.Lerp(this.globalCamera.position, cameraStartPosition, 1.2 * deltaTime)
        this.globalCamera.fov = Scalar.Lerp(this.globalCamera.fov, (cameraStartFov.x + (this.props.isLandscape ? 0 : configData.portraitAnimationCameraFOV)) * Math.PI / 180, 1.2 * deltaTime) 
        
        if(this.globalSoloModelAnimations[productID]['animationDeltaTime'] >= animationTime){
          this.globalSoloModelAnimations[productID]['duplicated'].setEnabled(true)
          this.globalSoloModelAnimations[productID]['animationState'] = 2
          this.globalSoloModelAnimations[productID]['animationDeltaTime'] = 0
          animationGroup.play(false)
        }
      }
      if(animationState === 2){ //OK
        //Animation
        if(cameraPosition && cameraTarget && cameraFov && cameraSync.scaling.x === 0){
          this.globalCamera.target = cameraTarget.getAbsolutePosition()
          this.globalCamera.lockedTarget = cameraTarget.getAbsolutePosition()
          this.globalCamera.position = cameraPosition.getAbsolutePosition()
          this.globalCamera.fov = (cameraFov.scaling.x + (this.props.isLandscape ? 0 : configData.portraitAnimationCameraFOV)) * Math.PI / 180
        }
      }
      if(animationState === 3){
        //End
        const targetPosition = new Vector3(configData.bullettarget.x, configData.bullettarget.y, configData.bullettarget.z);
        this.globalCamera.target = Vector3.Lerp(this.globalCamera.target, targetPosition, 1.2 * deltaTime)
        this.globalCamera.lockedTarget = Vector3.Lerp(this.globalCamera.lockedTarget, targetPosition, 1.2 * deltaTime)
        this.globalCamera.position = Vector3.Lerp(this.globalCamera.position, cameraUserPosition, 1.2 * deltaTime)
        this.globalCamera.fov = Scalar.Lerp(this.globalCamera.fov, + (this.props.isLandscape ? configData.bulletCameraFOV : configData.portraitBulletCameraFOV) * Math.PI / 180, 1.2 * deltaTime) 

        this.globalSoloModelAnimations[productID]['duplicated'].setEnabled(false)
        if(this.globalSoloModelAnimations[productID]['animationDeltaTime'] >= animationTime){
          this.ForceStopAnimatingSoloProduct(false)
          // this.globalSoloModelAnimations[productID]['animationState'] = 0
          // this.globalSoloModelAnimations[productID]['animationDeltaTime'] = 0
          // this.UpdateActiveBullets(true)
          // this.globalCamera.upperRadiusLimit = configData.bulletlimitradius.upper
          // this.globalCamera.attachControl(this.canvasRef.current, true);
        }
      }        
    }

    if(this.globalScenarioConfig.is360 === true && this.globalCamera.keysUp.length === 0){
      this.globalCamera.alpha = this.Lerp(this.globalCamera.alpha, this.globalCamera.alpha + this.globalCameraRight, 0.01);
      this.globalCamera.beta = this.Lerp(this.globalCamera.beta, this.globalCamera.beta + this.globalCameraUp, 0.01);
    }

    //PERFORMANCE CONTROL
    this.globalPerformanceControl.avgFrameTime += deltaTime
    this.globalPerformanceControl.framesCount += 1
    if (this.globalPerformanceControl.framesCount > this.globalPerformanceControl.frameSamples - 1) {
      const avgFrameRate = this.globalPerformanceControl.framesCount / this.globalPerformanceControl.avgFrameTime;
      this.globalPerformanceControl.framesCount = 0
      this.globalPerformanceControl.avgFrameTime = 0

      const scaleChage = avgFrameRate > this.globalPerformanceControl.targetFramerate ? -0.1 : 0.1;
      this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.scalingLevel + scaleChage
      if (this.globalPerformanceControl.scalingLevel > this.globalPerformanceControl.maxScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.maxScalingLevel
      if (this.globalPerformanceControl.scalingLevel < this.globalPerformanceControl.minScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.minScalingLevel

      if (this.globalPerformanceControl.scalingLevel !== this.globalEngine.getHardwareScalingLevel()) {// && browserInfo.isOSiOS === false
        this.globalEngine.setHardwareScalingLevel(this.globalPerformanceControl.scalingLevel);
      }
    }
  }

  LookAtCamera = (object: Mesh, camera : ArcRotateCamera) => {
    object.lookAt(camera.position)
    object.rotation = object.rotation.add(new Vector3(0.5 * Math.PI, 0, 0));
  }

  LoadCubeMapAsync = async (texturesUrl: string, scene: Scene): Promise<CubeTexture> => {
    return new Promise((resolve, reject) => {
      const cubeTexture = new CubeTexture(texturesUrl, scene, null, true, null,
        () => { resolve(cubeTexture) },
        (message, exception) => reject(new Error("Error loadingcube texture: " + message))
      )
    })
  }

  LoadTextureAsync = async (textureUrl: string, scene: Scene, invertY: boolean): Promise<Texture> => {
    return new Promise((resolve, reject) => {
      const texture: Texture = new Texture(textureUrl, scene, true, invertY, Texture.BILINEAR_SAMPLINGMODE,
        () => resolve(texture),
        (message, exception) => reject(new Error("Error loading texture: " + message))
      );
    })
  }

  LoadAllTexturesAndModels = async (scene: Scene, scenario: any): Promise<void> => {

    let mobileFolder = browserInfo.isDeviceMobile === true ? "mobile/" : ""
    let iOSFolder = browserInfo.isOSiOS === true ? "mobile/" : ""
    console.log("Loading HDRI")
    //const cubeTexture = await this.LoadCubeMapAsync('./Textures/HDRI/', scene);
    const cubeTexture = new HDRCubeTexture(iOSFolder ? "./Textures/HDRI/brown_photostudio_128.hdr" : "./Textures/HDRI/brown_photostudio_512.hdr",
                                            scene, 128, false, true, false, true);
    scene.environmentTexture = cubeTexture
    scene.environmentIntensity = configData.environmentIntensity

    {
      console.log("Loading SoloModels")
      this.globalBullets = {}

      const planeMaterial = new StandardMaterial('bullet', scene);
      planeMaterial.diffuseTexture = await this.LoadTextureAsync(configData.bulletTexture, scene, false);
      planeMaterial.diffuseTexture.hasAlpha = true
      planeMaterial.transparencyMode = 2
      planeMaterial.disableLighting = true
      planeMaterial.useAlphaFromDiffuseTexture = true;
      planeMaterial.emissiveColor = new Color3(1,1,1)

      // Animation
      let keys = [];
      keys.push({ frame: 0, value: new Vector3(configData.bulletAnimationScale.max, configData.bulletAnimationScale.max, configData.bulletAnimationScale.max)});
      keys.push({ frame: 30, value: new Vector3(configData.bulletAnimationScale.min, configData.bulletAnimationScale.min, configData.bulletAnimationScale.min) });
      keys.push({ frame: 60, value: new Vector3(configData.bulletAnimationScale.max, configData.bulletAnimationScale.max, configData.bulletAnimationScale.max)});
      let scaleAnimation = new Animation("scaleAnimation", "scaling", 30, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
      scaleAnimation.setKeys(keys);

      const { meshes, animationGroups} = await SceneLoader.ImportMeshAsync('', './models/', configData.soloModel, scene) //const { meshes,  } = await SceneLoader.ImportMeshAsync('', './models/', 'Character.gltf', this.sceneGlobal)
      this.globalSoloModelAnimations = {};
      this.globalSoloModelColors = {}
      const meshesRoot = meshes[0] as Mesh
      for (let i = 0; i < meshesRoot.getChildren().length; i++) {
        const meshParent = meshesRoot.getChildren()[i] as Mesh

        if (meshParent.name.toLocaleLowerCase().includes("products_roof")) {
          this.globalSoloModel = meshParent;
          this.globalSoloModel.setEnabled(false)
          for (let p = 0; p < meshParent.getChildren().length; p++) {
            const meshProduct = meshParent.getChildren()[p] as Mesh
            const bullets = bulletData[meshProduct.name.toLocaleLowerCase()];
            if(bullets){
              let bullet_array = []
              for (const bullet of bullets) {
                const plane = MeshBuilder.CreateGround(bullet.id, { height: configData.bulletSize.height, width: configData.bulletSize.width });
                plane.position = new Vector3(bullet.position.x, bullet.position.y, bullet.position.z)
                plane.material = planeMaterial;
                //plane.parent = meshProduct  //lookAt falla con padre, aún usando space.World
                plane.animations = [];
                plane.animations.push(scaleAnimation);
                plane.setEnabled(true)
                bullet_array.push(plane)
                scene.beginAnimation(plane, 0, 60, true);
              }
              this.globalBullets[meshProduct.name.toLocaleLowerCase()] = bullet_array
            }
            for (let productDat of productData){
              
              if(productDat.id === meshProduct.name.toLocaleLowerCase()){
                this.globalSoloModelAnimations[productDat.id] = {};
                this.globalSoloModelColors[productDat.id] = {};
                let decalsParent = new TransformNode("staticdecals");//guardar esto en algún lado global
                let logoColors = ["white", "gray", "blue"]
                for (let logoColor of logoColors){
                  const decalMaterial = new StandardMaterial('decalMaterial'+logoColor+productDat.id, scene);
                  decalMaterial.diffuseTexture = await this.LoadTextureAsync('./Textures/decal_logo_'+logoColor+'.png', scene, true);
                  decalMaterial.transparencyMode = 2
                  decalMaterial.diffuseTexture.hasAlpha = true
                  decalMaterial.disableLighting = true
                  decalMaterial.useAlphaFromDiffuseTexture = true;
                  decalMaterial.emissiveColor = new Color3(0.9,0.9,0.9)
                  const decalMesh = MeshBuilder.CreateGround("decalMesh"+logoColor+productDat.id, { height: 1, width: 1});
                  decalMesh.material = decalMaterial;
                  decalMesh.setEnabled(false);
                  decalMesh.setParent(decalsParent)
                }
                decalsParent.position = new Vector3(productDat.decal.position.x, productDat.decal.position.y, productDat.decal.position.z)
                decalsParent.rotation = new Vector3(productDat.decal.rotation.x * Math.PI / 180, productDat.decal.rotation.y * Math.PI / 180, productDat.decal.rotation.z * Math.PI / 180)
                decalsParent.scaling = new Vector3(productDat.decal.scaling.x, productDat.decal.scaling.y, productDat.decal.scaling.z)
                decalsParent.setParent(meshProduct)
                
                let colorMeshes = []
                for(let mainPartMesh of meshProduct.getChildren() ){
                  if(mainPartMesh.name.includes('texture')){//inside main child
                    colorMeshes.push(mainPartMesh)
                    for(let partMesh of mainPartMesh.getChildren() ){
                      if(partMesh.name.toLocaleLowerCase().includes("animation")){
                        for(let animationPart of partMesh.getChildren() ){
                          if(animationPart.name.toLocaleLowerCase().includes("cameraposition")){
                            this.globalSoloModelAnimations[productDat.id]['cameraposition'] = animationPart;
                            this.globalSoloModelAnimations[productDat.id]['startcameraposition'] = (animationPart as TransformNode).getAbsolutePosition().clone();
                          }  
                          if(animationPart.name.toLocaleLowerCase().includes("cameratarget")){
                            this.globalSoloModelAnimations[productDat.id]['cameratarget'] = animationPart;
                            this.globalSoloModelAnimations[productDat.id]['startcameratarget'] = (animationPart as TransformNode).getAbsolutePosition().clone();
                          }
                          if(animationPart.name.toLocaleLowerCase().includes("camerafov")){
                            this.globalSoloModelAnimations[productDat.id]['camerafov'] = animationPart;
                            this.globalSoloModelAnimations[productDat.id]['startcamerafov'] = (animationPart as TransformNode).scaling.clone();
                          }
                          if(animationPart.name.toLocaleLowerCase().includes("camerasync"))
                            this.globalSoloModelAnimations[productDat.id]['camerasync'] = animationPart;
                          if(animationPart.name.toLocaleLowerCase().includes("texture")){
                            this.globalSoloModelAnimations[productDat.id]['duplicated'] = animationPart;
                            colorMeshes.push(animationPart)
                          }
                          }
                        }
                    }
                  }                  
                }
                this.globalSoloModelColors[productDat.id]['meshes'] = colorMeshes;
                break;
              }
            }
          }
        }
      }
      //Load Animations
      for(let animationGroup of animationGroups){
        animationGroup.stop();
        animationGroup.onAnimationGroupEndObservable.add(this.EndAnimationSoloProduct);
        for (let productDat of productData){
          if(animationGroup.name.toLowerCase().includes(productDat.id)){
            this.globalSoloModelAnimations[productDat.id]['animationState'] = 0
            this.globalSoloModelAnimations[productDat.id]['animationGroup'] = animationGroup
          }
        }
      }

      if (scenario.is360 === true && this.globalCamera){
        this.globalCamera.keysUp = []; // Tecla flecha abajo
        this.globalCamera.keysDown = []; // Tecla flecha arriba
        this.globalCamera.keysLeft = []; // Tecla flecha derecha
        this.globalCamera.keysRight = []; // Tecla flecha izquierda
        this.globalCamera.angularSensibilityX = 5000
        this.globalCamera.angularSensibilityY = 5000

        this.globalDomeHotspots = {}
        for(const spotKey in scenario.hotspots360){
          let hotspot_array = []
          for (const space of scenario.hotspots360[spotKey].hotspots) {
            const plane = MeshBuilder.CreateGround(space.name, { height: configData.hotspotSize.height, width: configData.hotspotSize.width });
            plane.position = new Vector3(space.position.x, space.position.y, space.position.z)
            plane.material = planeMaterial;
            plane.animations = [];
            plane.animations.push(scaleAnimation);
            plane.setEnabled(true)
            hotspot_array.push(plane)
            scene.beginAnimation(plane, 0, 60, true);
          }
          this.globalDomeHotspots[spotKey] = {
            rotation: scenario.hotspots360[spotKey].rotation,
            hotspots: hotspot_array
          };
        }
      }

      //Load Solo Textures
      for (let productRoof of scenario.productsRoof){
        const productColors = colorData[productRoof]
        if(productColors){
          for (let productColor of productColors){
            const texture = await this.LoadTextureAsync("./Textures/Products/"+productRoof+"/"+mobileFolder+productColor.id+".jpg", scene, false);
            if(texture){
              texture.coordinatesIndex = 1;
              this.globalSoloModelColors[productRoof][productColor.id] = texture;
            }
          }  
        }
      }
      for (let productFacade of scenario.productsFacade){
        const productColors = colorData[productFacade]
        if(productColors){
          for (let productColor of productColors){
            const texture = await this.LoadTextureAsync("./Textures/Products/"+productFacade+"/"+mobileFolder+productColor.id+".jpg", scene, false);
            if(texture){
              texture.coordinatesIndex = 1;
              this.globalSoloModelColors[productFacade][productColor.id] = texture;
            }
          }  
        }
      }
    }   

    if (scenario.is360 === false){
      console.log("Loading Scenario " + scenario.name)
      const { meshes } = await SceneLoader.ImportMeshAsync('', './models/', scenario.modelfile, scene)
      const meshesRoot = meshes[0] as Mesh
      this.globalScenario = meshesRoot
      for (let i = 0; i < meshesRoot.getChildren().length; i++) {
        const meshParent = meshesRoot.getChildren()[i] as Mesh

        if (meshParent.name.toLocaleLowerCase().includes("products_roof")) {
          this.globalRoof = meshParent;
        }else if (meshParent.name.toLocaleLowerCase().includes("products_facade")) {
          this.globalFacade = meshParent;
        }
      }
    }else{
      this.globalDomeTextures = {}
      for (let productRoof of scenario.productsRoof){
        for (let productD of productData){
          if(productRoof === productD.id){
            this.globalDomeTextures[productRoof] = {}
            let folder360 = productD.folder360;
            //Load 360 images for Coworking
            for (let colorID of colorData[productRoof]){
              let text01 = await this.LoadTextureAsync(folder360 + colorID.id +"/"+ iOSFolder + "01.jpg", scene, true);
              let text02 = await this.LoadTextureAsync(folder360 + colorID.id +"/"+ iOSFolder + "02.jpg", scene, true);
              let text03 = await this.LoadTextureAsync(folder360 + colorID.id +"/"+ iOSFolder + "03.jpg", scene, true);
              this.globalDomeTextures[productRoof][colorID.id] = {
                space01: text01,
                space02: text02,
                space03: text03,
              };
            }
            break;
          }
        }
      }
      let startingText = this.globalDomeTextures[this.props.roofProduct.id][this.props.roofColor.id][this.globalDomeSpot];
      this.globalDome = new PhotoDome( "dome360",
        startingText.name,
        {
            resolution: 64,
            size: 100,
            useDirectMapping: false
        }, scene
      );
      this.globalDomeFade = new PhotoDome( "domeFade360",
        startingText.name,
        {
            resolution: 64,
            size: 99,
            useDirectMapping: false
        }, scene
      );

      this.globalCamera?.inputs.attached.mousewheel.detachControl();
  
      if(this.globalCamera){
        this.globalCamera.alpha = 0
        this.globalCamera.beta = 0.5*Math.PI
        this.globalCamera.upperRadiusLimit = 0.1
        this.globalCamera.radius = 0.1
        this.globalCamera.lockedTarget = Vector3.Zero()
        this.globalCamera.fov = 60 * Math.PI / 180
        this.globalCamera.upperBetaLimit =  Math.PI
        this.globalCamera.lowerBetaLimit =  0
      }
      this.UpdateActiveHotspots(this.globalDomeSpot, -1)
    }
    
    this.UpdateCategoryProduct(this.globalSoloModel, this.props.roofProduct, this.props.roofColor)
    this.UpdateCategoryProduct(this.globalRoof, this.props.roofProduct, this.props.roofColor)
    this.UpdateCategoryProduct(this.globalFacade, this.props.facadeProduct, this.props.facadeColor)
    this.UpdateActiveBullets(false)

    this.setState({ sceneLoaded: true })
    this.props.setShowLoadingScreen(false)
    console.log("Loading Completed")
  }

  render(): React.ReactNode {
    return (
      <div className={`scene3d-container ${this.state.sceneLoaded ? 'visible' : ''}`}>
        <canvas ref={this.canvasRef} style={{ width: '100%', height: '100%' }}></canvas>
      </div>
    )
  }
}

export default Scene3D